import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

// Data elements animations
const barPulse = keyframes`
  0%, 100% { transform: scaleY(1); opacity: 0.4; }
  50% { transform: scaleY(1.3); opacity: 0.7; }
`;

const bubbleFloat = keyframes`
  0% { transform: translateY(100vh) scale(0.5); opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { transform: translateY(-100px) scale(1); opacity: 0; }
`;

const lineFlow = keyframes`
  0% { stroke-dashoffset: 2000; }
  100% { stroke-dashoffset: 0; }
`;

const gridPulse = keyframes`
  0%, 100% { opacity: 0.1; }
  50% { opacity: 0.25; }
`;

const dataPointPop = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.5); opacity: 0.9; }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const shootingStar = keyframes`
  0% { transform: translate(0, 0) rotate(-45deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translate(200px, 200px) rotate(-45deg); opacity: 0; }
`;

// Falcon flying animation - bottom-left to top-right
const falconFly = keyframes`
  0% {
    transform: translate(0, 0);
    opacity: 0;
  }
  5% {
    opacity: 0.85;
  }
  95% {
    opacity: 0.85;
  }
  100% {
    transform: translate(110vw, -75vh);
    opacity: 0;
  }
`;

export const FalconDataScene = () => {
  const barHeights = [45, 70, 55, 85, 40, 95, 60, 75, 50, 88, 65, 45, 80, 55, 70];

  const stars = [
    { top: '5%', left: '10%', size: 3, delay: 0 },
    { top: '8%', left: '25%', size: 2, delay: 0.5 },
    { top: '3%', left: '40%', size: 4, delay: 1 },
    { top: '10%', left: '55%', size: 2, delay: 1.5 },
    { top: '6%', left: '70%', size: 3, delay: 0.8 },
    { top: '4%', left: '85%', size: 2, delay: 2 },
    { top: '12%', left: '15%', size: 2, delay: 1.2 },
    { top: '15%', left: '35%', size: 3, delay: 0.3 },
    { top: '11%', left: '60%', size: 2, delay: 1.8 },
    { top: '8%', left: '78%', size: 4, delay: 0.6 },
    { top: '18%', left: '5%', size: 2, delay: 2.2 },
    { top: '14%', left: '92%', size: 3, delay: 1.1 },
    { top: '2%', left: '50%', size: 3, delay: 0.9 },
    { top: '16%', left: '45%', size: 2, delay: 1.6 },
    { top: '7%', left: '95%', size: 2, delay: 0.4 },
  ];

  const bubbles = [
    { size: 20, left: '10%', delay: 0, duration: 15 },
    { size: 35, left: '25%', delay: 3, duration: 18 },
    { size: 15, left: '40%', delay: 6, duration: 12 },
    { size: 28, left: '55%', delay: 2, duration: 20 },
    { size: 22, left: '70%', delay: 8, duration: 16 },
    { size: 18, left: '85%', delay: 5, duration: 14 },
    { size: 30, left: '15%', delay: 10, duration: 17 },
    { size: 25, left: '60%', delay: 12, duration: 19 },
    { size: 16, left: '80%', delay: 4, duration: 13 },
    { size: 32, left: '35%', delay: 7, duration: 21 },
  ];

  const dataPoints = [
    { x: '15%', y: '25%', delay: 0 },
    { x: '30%', y: '45%', delay: 1.5 },
    { x: '45%', y: '30%', delay: 0.8 },
    { x: '60%', y: '55%', delay: 2 },
    { x: '75%', y: '35%', delay: 1.2 },
    { x: '85%', y: '50%', delay: 0.5 },
    { x: '20%', y: '60%', delay: 2.5 },
    { x: '50%', y: '70%', delay: 1.8 },
  ];

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      overflow="hidden"
      pointerEvents="none"
    >
      {/* Ambient background gradients */}
      <Box
        position="absolute"
        top="-30%"
        left="-20%"
        w="70%"
        h="70%"
        bgGradient="radial(circle, brand.500 0%, transparent 70%)"
        filter="blur(120px)"
        opacity={0.3}
      />
      <Box
        position="absolute"
        bottom="-30%"
        right="-20%"
        w="80%"
        h="80%"
        bgGradient="radial(circle, accent.500 0%, transparent 70%)"
        filter="blur(150px)"
        opacity={0.25}
      />

      {/* Grid background */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        animation={`${gridPulse} 8s ease-in-out infinite`}
        sx={{
          backgroundImage: `
            linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Twinkling stars at the top */}
      {stars.map((star, i) => (
        <Box
          key={`star-${i}`}
          position="absolute"
          top={star.top}
          left={star.left}
          w={`${star.size}px`}
          h={`${star.size}px`}
          borderRadius="full"
          bg="white"
          boxShadow={`0 0 ${star.size * 2}px rgba(255, 255, 255, 0.8), 0 0 ${star.size * 4}px rgba(99, 102, 241, 0.5)`}
          animation={`${twinkle} ${2 + star.delay}s ease-in-out infinite ${star.delay}s`}
        />
      ))}

      {/* Shooting stars - big end falls first */}
      <Box
        position="absolute"
        top="5%"
        left="20%"
        w="3px"
        h="40px"
        bgGradient="linear(to-t, white, transparent)"
        animation={`${shootingStar} 8s linear infinite 2s`}
        opacity={0.7}
        borderRadius="full"
      />
      <Box
        position="absolute"
        top="10%"
        left="70%"
        w="2px"
        h="30px"
        bgGradient="linear(to-t, white, transparent)"
        animation={`${shootingStar} 12s linear infinite 6s`}
        opacity={0.5}
        borderRadius="full"
      />

      {/* Flying falcon - overhead silhouette */}
      <Box
        position="absolute"
        bottom="10%"
        left="-80px"
        animation={`${falconFly} 15s ease-in-out infinite`}
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 100 100"
          style={{ transform: 'rotate(65deg)' }}
        >
          <defs>
            <linearGradient id="falconGradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#a78bfa" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#e9d5ff" stopOpacity="0.95" />
            </linearGradient>
            <filter id="falconGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Overhead falcon - head pointing up */}
          <path
            d="M50 8
               C52 8 54 12 54 18
               L54 30
               C58 28 75 20 88 15
               C92 14 95 16 93 20
               C88 28 70 38 58 42
               L56 42 L56 70
               C56 72 58 78 60 82
               L54 78 L50 85 L46 78 L40 82
               C42 78 44 72 44 70
               L44 42 L42 42
               C30 38 12 28 7 20
               C5 16 8 14 12 15
               C25 20 42 28 46 30
               L46 18
               C46 12 48 8 50 8Z"
            fill="url(#falconGradient)"
            filter="url(#falconGlow)"
          />
        </svg>
      </Box>

      {/* Flowing line charts - Layer 1 (back) with light pulse */}
      <Box position="absolute" top="20%" left={0} right={0} opacity={0.4}>
        <svg width="100%" height="200" viewBox="0 0 1200 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#d946ef" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient id="lineGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#d946ef" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.6" />
            </linearGradient>
            {/* Glowing pulse gradient */}
            <radialGradient id="pulseGlow1">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="40%" stopColor="#8b5cf6" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="pulseGlow2">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="40%" stopColor="#d946ef" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#d946ef" stopOpacity="0" />
            </radialGradient>
            <filter id="glow1" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {/* Base lines */}
          <path
            id="line1"
            d="M0 150 Q100 100 200 120 T400 80 T600 130 T800 60 T1000 100 T1200 70"
            fill="none"
            stroke="url(#lineGradient1)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="2000"
            style={{ animation: `${lineFlow} 8s linear infinite` }}
          />
          <path
            id="line2"
            d="M0 100 Q150 150 300 90 T600 140 T900 70 T1200 120"
            fill="none"
            stroke="url(#lineGradient2)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="2000"
            style={{ animation: `${lineFlow} 12s linear infinite` }}
          />
          {/* Fast pulse on line 1 */}
          <circle r="6" fill="url(#pulseGlow1)" filter="url(#glow1)">
            <animateMotion dur="4s" repeatCount="indefinite">
              <mpath href="#line1" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="4s" repeatCount="indefinite" />
          </circle>
          {/* Slow pulse on line 1 */}
          <circle r="10" fill="url(#pulseGlow1)" filter="url(#glow1)">
            <animateMotion dur="16s" repeatCount="indefinite" begin="2s">
              <mpath href="#line1" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.05;0.95;1" dur="16s" repeatCount="indefinite" begin="2s" />
          </circle>
          {/* Medium pulse on line 2 */}
          <circle r="7" fill="url(#pulseGlow2)" filter="url(#glow1)">
            <animateMotion dur="10s" repeatCount="indefinite" begin="1s">
              <mpath href="#line2" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="10s" repeatCount="indefinite" begin="1s" />
          </circle>
          {/* Very slow pulse on line 2 */}
          <circle r="12" fill="url(#pulseGlow2)" filter="url(#glow1)">
            <animateMotion dur="22s" repeatCount="indefinite" begin="5s">
              <mpath href="#line2" />
            </animateMotion>
            <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.05;0.95;1" dur="22s" repeatCount="indefinite" begin="5s" />
          </circle>
        </svg>
      </Box>

      {/* Rising bubbles */}
      {bubbles.map((bubble, i) => (
        <Box
          key={`bubble-${i}`}
          position="absolute"
          left={bubble.left}
          bottom="-50px"
          w={`${bubble.size}px`}
          h={`${bubble.size}px`}
          borderRadius="full"
          bgGradient="radial(circle at 30% 30%, accent.300, brand.500)"
          boxShadow="0 0 20px rgba(99, 102, 241, 0.4)"
          animation={`${bubbleFloat} ${bubble.duration}s ease-in-out infinite ${bubble.delay}s`}
        />
      ))}

      {/* Bar chart - left side */}
      <Box
        position="absolute"
        bottom="5%"
        left="3%"
        display="flex"
        alignItems="flex-end"
        gap="6px"
        opacity={0.5}
      >
        {barHeights.slice(0, 8).map((height, i) => (
          <Box
            key={`bar-left-${i}`}
            w="14px"
            h={`${height}px`}
            bgGradient="linear(to-t, brand.600, brand.400, accent.400)"
            borderRadius="4px 4px 0 0"
            animation={`${barPulse} ${2.5 + i * 0.3}s ease-in-out infinite ${i * 0.15}s`}
            transformOrigin="bottom"
          />
        ))}
      </Box>

      {/* Bar chart - right side */}
      <Box
        position="absolute"
        bottom="8%"
        right="5%"
        display="flex"
        alignItems="flex-end"
        gap="8px"
        opacity={0.4}
      >
        {barHeights.slice(7, 15).map((height, i) => (
          <Box
            key={`bar-right-${i}`}
            w="10px"
            h={`${height * 0.8}px`}
            bgGradient="linear(to-t, accent.600, accent.400, brand.300)"
            borderRadius="3px 3px 0 0"
            animation={`${barPulse} ${3 + i * 0.25}s ease-in-out infinite ${i * 0.2}s`}
            transformOrigin="bottom"
          />
        ))}
      </Box>

      {/* Data points */}
      {dataPoints.map((point, i) => (
        <Box
          key={`point-${i}`}
          position="absolute"
          left={point.x}
          top={point.y}
          w="12px"
          h="12px"
          borderRadius="full"
          bgGradient="radial(circle, white, brand.400)"
          boxShadow="0 0 15px rgba(99, 102, 241, 0.6), 0 0 30px rgba(217, 70, 239, 0.3)"
          animation={`${dataPointPop} ${2 + i * 0.2}s ease-in-out infinite ${point.delay}s`}
        />
      ))}

      {/* Flowing line chart - Layer 2 (front) with light pulse */}
      <Box position="absolute" bottom="25%" left={0} right={0} opacity={0.5}>
        <svg width="100%" height="150" viewBox="0 0 1200 150" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lineGradient3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#e879f9" />
            </linearGradient>
            <radialGradient id="pulseGlow3">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="30%" stopColor="#a78bfa" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#818cf8" stopOpacity="0" />
            </radialGradient>
            <filter id="glow2" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {/* Base line */}
          <path
            id="line3"
            d="M0 80 Q200 30 400 90 T800 40 T1200 100"
            fill="none"
            stroke="url(#lineGradient3)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="2000"
            style={{ animation: `${lineFlow} 6s linear infinite` }}
          />
          {/* Light pulse traveling along line 3 */}
          <circle r="10" fill="url(#pulseGlow3)" filter="url(#glow2)">
            <animateMotion dur="6s" repeatCount="indefinite">
              <mpath href="#line3" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.05;0.95;1" dur="6s" repeatCount="indefinite" />
          </circle>
          {/* Second pulse with offset */}
          <circle r="7" fill="url(#pulseGlow3)" filter="url(#glow2)">
            <animateMotion dur="6s" repeatCount="indefinite" begin="3s">
              <mpath href="#line3" />
            </animateMotion>
            <animate attributeName="opacity" values="0;0.8;0.8;0" keyTimes="0;0.05;0.95;1" dur="6s" repeatCount="indefinite" begin="3s" />
          </circle>
        </svg>
      </Box>
    </Box>
  );
};

export default FalconDataScene;
